import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useGetPermissionCatalog,
  useInviteMember,
} from "@/services/api/teamService"
import { OrgRole } from "@/types/team"
import { IconShieldCheck } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { PermissionEditor } from "./PermissionEditor"
import { closeDependencies } from "./permissionResolver"
import { RoleChooser } from "./RoleChooser"
import { getTeamErrorCode } from "./teamErrors"

interface InviteMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InviteMemberDialog({
  open,
  onOpenChange,
}: InviteMemberDialogProps) {
  const { translate } = useTranslation()
  const inviteMutation = useInviteMember()
  const { data: catalog } = useGetPermissionCatalog(open)

  const [email, setEmail] = useState("")
  const [role, setRole] = useState<OrgRole>("member")
  const [permissions, setPermissions] = useState<string[]>([])

  // Default the starting access to the BASE block (View only) — replaces the
  // Viewer preset placeholder. New invitees see everything, change nothing;
  // the admin ticks add-on blocks as needed.
  const baseBlock = catalog?.roleBlocks.find((b) => b.key === "BASE")
  useEffect(() => {
    if (open && catalog && baseBlock) {
      setPermissions(closeDependencies(baseBlock.permissions, catalog))
    }
  }, [open, catalog, baseBlock])

  const reset = () => {
    setEmail("")
    setRole("member")
    setPermissions(
      catalog && baseBlock
        ? closeDependencies(baseBlock.permissions, catalog)
        : [],
    )
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    inviteMutation.mutate(
      {
        email: email.trim(),
        role,
        permissions: role === "member" ? permissions : [],
      },
      {
        onSuccess: () => {
          toast.success(
            translate("TEAM/INVITE_SUCCESS", { email: email.trim() }),
          )
          handleOpenChange(false)
        },
        onError: (err) => {
          const code = getTeamErrorCode(err)
          if (code === "ALREADY_MEMBER")
            toast.error(translate("TEAM/ALREADY_MEMBER"))
          else if (code === "SEAT_LIMIT_REACHED")
            toast.error(translate("TEAM/SEAT_LIMIT_TITLE"))
          // A 5xx is already toasted by the API client.
          else toastApiError(err, translate, "TEAM/INVITE_ERROR")
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{translate("TEAM/INVITE_TITLE")}</DialogTitle>
            <DialogDescription>
              {translate("TEAM/INVITE_DESCRIPTION")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">
                {translate("TEAM/EMAIL_LABEL")}
              </Label>
              <Input
                id="invite-email"
                type="email"
                required
                autoFocus
                placeholder={translate("TEAM/EMAIL_PLACEHOLDER")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{translate("TEAM/ROLE_LABEL")}</Label>
              <RoleChooser value={role} onChange={setRole} />
            </div>

            {role === "member" ? (
              <div className="space-y-2">
                <Label>{translate("TEAM/STARTING_ACCESS")}</Label>
                {catalog ? (
                  <PermissionEditor
                    catalog={catalog}
                    value={permissions}
                    onChange={setPermissions}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {translate("TEAM/LOADING")}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                <IconShieldCheck size={18} className="mt-0.5 shrink-0" />
                <span>{translate("TEAM/ADMIN_ACCESS_BODY")}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {translate("TEAM/CANCEL")}
            </Button>
            <PendingButton type="submit" pending={inviteMutation.isPending}>
              {translate("TEAM/SEND_INVITE")}
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
